import moment from "moment";
import React, { useEffect, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import InputMask from "react-input-mask";
import clsx from "clsx";
import cloneDeep from "lodash.clonedeep";

import {
  phoneNumRegexp,
  phoneCleaner,
  handleKey,
  checkIsIncEin,
  checkIsIncLLCIncorporation,
  parseBooleanValue,
} from "_helpers";

import FormPDF from "components/OrderDetails/ProductDetails/FormPDF";
import ActivityIncEdit from "components/OrderDetails/ProductDetails/Incorporation/Activity/Edit";
import DateEdit from "components/OrderDetails/ProductDetails/Incorporation/Date/Edit";

import StatusEdit from "../../Status/Edit";

import { state_array, designatorsLLC, suffixArray, TAB_CODES } from "variables/Variables";

const getYearOptions = () => {
  const startYear = new Date().getFullYear();

  return Array.from(new Array(20), (_, i) => {
    return { value: startYear + i, label: `${startYear + i}` };
  });
};

const getMonthOptions = () => [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function FormLLCInc({ orderDetails, selectedProduct, onError, onChange, onSelectedProduct, ...props }) {
  const [membersCollapsed, setMembersCollapsed] = useState(true);
  const [newMember, setNewMember] = useState("");
  const [newOwnerFirstName, setNewOwnerFirstName] = useState("");
  const [newOwnerMiddleName, setNewOwnerMiddleName] = useState("");
  const [newOwnerLastName, setNewOwnerLastName] = useState("");

  const productDetails = orderDetails?.products[selectedProduct];
  const llcEINProductIndex = orderDetails?.products?.findIndex((p) => checkIsIncEin(p.code.code));
  const hasLlcEINProduct = Boolean(llcEINProductIndex >= 0);

  const einProductIndex = orderDetails?.products?.findIndex((p) => p.code.code === "EIN_LLC");
  const hasEinProduct = Boolean(einProductIndex >= 0);
  const hasOwners = productDetails?.members?.some(({ firstName }) => !!firstName);
  const isIncLLC = checkIsIncLLCIncorporation(productDetails?.code?.code);

  const getManagedBy = () => {
    if (!productDetails?.managedByManagers && !productDetails?.managedByMembers) return "";
    if (productDetails?.managedByManagers && productDetails?.managedByMembers) return "Members & Managers";
    if (productDetails?.members?.some(({ firstName }) => !!firstName)) return "Owners";
    return productDetails?.managedByMembers ? "Members" : "Managers";
  };

  const handleAddError = (field) => onError(TAB_CODES.llcIncInfo, field);

  const handleProductChange = ({ target: { name, value } }) => {
    const newObj = {
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct ? { ...product, [name]: value === "" ? null : value } : product
      ),
    };

    if (name === "activityPrimaryActivity") {
      newObj.products[selectedProduct].activitySpecificProducts = null;
      newObj.products[selectedProduct].productDescription = null;
      newObj.products[selectedProduct].productExtraQuestion1 = null;
      newObj.products[selectedProduct].productExtraQuestion2 = null;
      newObj.products[selectedProduct].productExtraQuestion3 = null;
    }

    if (name === "activitySpecificProducts") {
      newObj.products[selectedProduct].productDescription = null;
      newObj.products[selectedProduct].productExtraQuestion1 = null;
      newObj.products[selectedProduct].productExtraQuestion2 = null;
      newObj.products[selectedProduct].productExtraQuestion3 = null;
    }

    if (name === "companyName" && hasLlcEINProduct) {
      newObj.products[llcEINProductIndex].legalName = value;
    }

    if (name === "ssn") {
      newObj.products[selectedProduct].owner = { ...newObj.products[selectedProduct].owner, ssn: value };
    }

    onChange(newObj);
  };

  const checkCanUnset = () => {
    if (productDetails?.code?.code === "EIN_LLCIncorporation") {
      if (hasEinProduct) {
        const einProduct = orderDetails?.products[einProductIndex];
        if (
          productDetails?.processingResult?.status === "Completed" &&
          !productDetails?.processingResult?.logOfStatuses?.some((item) => item?.status === "SubmittedToState")
        ) {
          return false;
        }

        if (
          einProduct?.processingResult?.status === "Completed" &&
          !einProduct?.processingResult?.logOfStatuses?.some((item) => item?.status === "SubmittedToState")
        ) {
          return false;
        }

        return true;
      } else {
        if (
          productDetails?.processingResult?.status === "Completed" &&
          !productDetails?.processingResult?.logOfStatuses?.some((item) => item?.status === "SubmittedToState")
        ) {
          return false;
        }

        return true;
      }
    }

    if (checkIsIncLLCIncorporation(productDetails?.code?.code)) {
      if (
        productDetails?.processingResult?.status === "Completed" &&
        !productDetails?.processingResult?.logOfStatuses?.some((item) => item?.status === "SubmittedToState")
      ) {
        return false;
      }

      return true;
    }
  };

  // const handleProductOwnerChange = ({ target: { name, value } }) =>
  //   handleProductChange({
  //     target: {
  //       name: "owner",
  //       value: { ...productDetails.owner, [name]: name === "suffixName" && value === "" ? null : value },
  //     },
  //   });

  const handleBoiWasDeclinedByCustomerChange = ({ target: { value } }) => {
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct ? { ...product, boiWasDeclinedByCustomer: value === "Yes" } : product
      ),
    });
  };

  const handleMembershipBlockedChange = ({ target: { value } }) => {
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct ? { ...product, membershipBlocked: value === "Yes" } : product
      ),
    });
  };

  const handleManagedBy = ({ target: { value } }) => {
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? { ...product, managedByMembers: value === "Members", managedByManagers: value === "Managers" }
          : product
      ),
    });
  };

  const handleMemberDelete = (id) => {
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) => {
        if (hasLlcEINProduct && llcEINProductIndex === idx) {
          return { ...product, numberOfMembers: product.numberOfMembers - 1 };
        }

        if (idx === selectedProduct) {
          return { ...product, members: [...product.members.filter((_, idx) => idx !== id)] };
        }

        return product;
      }),
    });
  };

  const handleMemberAdd = (newMemberObj) => {
    const { name, firstName, middleName, lastName } = newMemberObj;
    const newObj = {};

    if (name) newObj.name = name.trim();
    if (firstName) newObj.firstName = firstName.trim();
    if (middleName) newObj.middleName = middleName.trim();
    if (lastName) newObj.lastName = lastName.trim();

    if (newObj.name || (newObj.firstName && newObj.lastName)) {
      onChange({
        ...orderDetails,
        products: orderDetails.products.map((product, idx) => {
          if (hasLlcEINProduct && llcEINProductIndex === idx) {
            return { ...product, numberOfMembers: product.numberOfMembers + 1 };
          }

          if (idx === selectedProduct) {
            return { ...product, members: [...product.members, newObj] };
          }

          return product;
        }),
      });
    }
  };

  const handleNewMemberKeyPressed = (e) => {
    if (handleKey(e)) {
      e.preventDefault();
      if (newMember) {
        handleMemberAdd({ name: newMember });
        setNewMember("");
      }
      if (newOwnerFirstName && newOwnerLastName) {
        handleMemberAdd({ firstName: newOwnerFirstName, middleName: newOwnerMiddleName, lastName: newOwnerLastName });
        setNewOwnerFirstName("");
        setNewOwnerMiddleName("");
        setNewOwnerLastName("");
      }
    }
  };

  const handlePrincipalOfficerChange = ({ target: { name, value } }) => {
    let newData = value;

    if (name === "phone") {
      newData = phoneCleaner(newData);
    }

    if (value === "") {
      newData = null;
    }

    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              principalOfficer: {
                ...product.principalOfficer,
                [name]: newData,
              },
            }
          : product
      ),
    });
  };

  const handleRegisteredAgentRemove = () => {
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              registeredAgent: null,
            }
          : product
      ),
    });
  };

  const handleRegisteredAgentChange = ({ target: { name, value } }) => {
    let newData = value;

    if (name === "phone") {
      newData = phoneCleaner(newData);
    }

    if (value === "") {
      newData = null;
    }

    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              registeredAgent: {
                ...product.registeredAgent,
                [name]: newData,
              },
            }
          : product
      ),
    });
  };

  const handleRegisteredAgentAddressChange = ({ target: { name, value } }) => {
    let newData = value;

    if (value === "") {
      newData = null;
    }

    const registeredAgentAddress = productDetails?.registeredAgent?.address || {};
    registeredAgentAddress[name] = newData;
    delete registeredAgentAddress["permanent"];
    delete registeredAgentAddress["country"];

    const isEmpty =
      newData === null &&
      !Object.values(registeredAgentAddress).some((item) => item !== undefined && item !== null && item !== "");

    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              registeredAgent: {
                ...product.registeredAgent,
                address: isEmpty
                  ? null
                  : {
                      ...(product?.registeredAgent?.address || {}),
                      [name]: newData,
                    },
              },
            }
          : product
      ),
    });
  };

  const handleCorporateAddressChange = ({ target: { name, value } }) =>
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              corporateAddress: {
                ...product.corporateAddress,
                [name]: value,
              },
            }
          : product
      ),
    });

  const handleMailingAddressChange = ({ target: { name, value } }) =>
    onChange({
      ...orderDetails,
      products: orderDetails.products.map((product, idx) =>
        idx === selectedProduct
          ? {
              ...product,
              mailingAddress: {
                ...product.mailingAddress,
                [name]: value,
              },
            }
          : product
      ),
    });

  const handleIncorporatedDate = (value) => {
    handleProductChange({ target: { name: "incorporatedDate", value } });
  };

  const handleWVFormationQuestionsChange = ({ target: { name, value } }) => {
    const newObj = {
      ...orderDetails,
      products: orderDetails.products.map((product, idx) => {
        if (idx === selectedProduct) {
          let newValue = value === "" ? null : value;

          if (value === "yes") {
            newValue = true;
          }

          if (value === "no") {
            newValue = false;
          }

          return {
            ...product,
            wvFormationQuestions: {
              ...(product?.wvFormationQuestions || {}),
              [name]: newValue,
            },
          };
        }

        return product;
      }),
    };

    onChange(newObj);
  };

  useEffect(() => {
    if (orderDetails.products?.length) {
      const currentProduct = orderDetails.products.find((_, idx) => idx === selectedProduct) || {};
      const sanitizedProduct = cloneDeep(currentProduct);

      if (!currentProduct.activityPrimaryActivity) {
        sanitizedProduct.activityPrimaryActivity = "other";
        sanitizedProduct.activitySpecificProducts = "other_other";
        sanitizedProduct.productDescription = "Other";

        onChange({
          ...orderDetails,
          products: orderDetails.products.map((product, idx) =>
            idx === selectedProduct
              ? {
                  ...product,
                  ...sanitizedProduct,
                }
              : product
          ),
        });
      }
    }
  }, [orderDetails, selectedProduct, onChange]);

  return (
    <div className="form-border">
      <h4 className="m-0 font-weight-bold mb-2">LLC Inc Corporation</h4>

      <StatusEdit
        orderDetails={orderDetails}
        selectedProduct={selectedProduct}
        onChange={onChange}
        onError={handleAddError}
        onSelectedProduct={onSelectedProduct}
      />

      <FormPDF productDetails={productDetails} {...props} />

      <hr />

      <DateEdit
        label="Incorporated Date:"
        date={productDetails?.incorporatedDate}
        isCanUnset={checkCanUnset()}
        onChange={handleIncorporatedDate}
      />

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Limited Liability Company Name:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="companyName"
            pattern="^[ a-zA-Z0-9\.\/,\\;\-:_'~˜`’\(\)\x22\[\]\?<>!\{\}@#\$%\^&\*\+=]+$" // \x22 is "
            value={productDetails.companyName || ""}
            onChange={handleProductChange}
            onInvalid={() => handleAddError("Company Name")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Alternate Company Name:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="altCompanyName"
            pattern="^[ a-zA-Z0-9\.\/,\\;\-:_'~˜`’\(\)\x22\[\]\?<>!\{\}@#\$%\^&\*\+=]+$" // \x22 is "
            value={productDetails.altCompanyName || ""}
            onChange={handleProductChange}
            onInvalid={() => handleAddError("Alternate Company Name")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Designator:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            required={isIncLLC}
            as="select"
            name="designator"
            value={productDetails?.designator || ""}
            onChange={handleProductChange}
            onInvalid={() => handleAddError("Designator")}
          >
            <option value="">Select a designator</option>
            {designatorsLLC.map((value, idx) => (
              <option key={`${idx}-${value}`} value={value}>
                {value}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Organized State:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required={isIncLLC}
            size="sm"
            as="select"
            name="organizedState"
            value={productDetails?.organizedState || ""}
            onChange={handleProductChange}
            onInvalid={() => handleAddError("Organized State")}
          >
            <option value="">Select a state</option>
            {Object.keys(state_array).map((key) => (
              <option key={key} value={key}>
                {state_array[key]}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Was BOI offer declined by customer?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="boiWasDeclinedByCustomer_r_yes"
            className="mr-3"
            type="radio"
            label="Yes"
            name="boiWasDeclinedByCustomer"
            value="Yes"
            defaultChecked={productDetails.boiWasDeclinedByCustomer}
            onChange={handleBoiWasDeclinedByCustomerChange}
          />
          <Form.Check
            id="boiWasDeclinedByCustomer_r_no"
            type="radio"
            label="No"
            name="boiWasDeclinedByCustomer"
            value="No"
            defaultChecked={!productDetails.boiWasDeclinedByCustomer}
            onChange={handleBoiWasDeclinedByCustomerChange}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Was Membership blocked for customer?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="membershipBlocked_r_yes"
            className={clsx("mr-3", { "text-danger": productDetails?.membershipBlocked })}
            type="radio"
            label="Yes"
            name="membershipBlocked"
            value="Yes"
            defaultChecked={productDetails?.membershipBlocked}
            onChange={handleMembershipBlockedChange}
          />
          <Form.Check
            id="membershipBlocked_r_no"
            type="radio"
            label="No"
            name="membershipBlocked"
            value="No"
            defaultChecked={!productDetails?.membershipBlocked}
            onChange={handleMembershipBlockedChange}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Managed By:
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          {hasOwners && (
            <Form.Check
              id="managedBy_r_owners"
              className="mr-3"
              type="radio"
              label="Owners"
              name="managedBy"
              value="Owners"
              defaultChecked={hasOwners}
              disabled={hasOwners}
            />
          )}
          {!hasOwners && (
            <>
              <Form.Check
                id="managedBy_r_members"
                className="mr-3"
                type="radio"
                label="Members"
                name="managedBy"
                value="Members"
                defaultChecked={productDetails.managedByMembers && !productDetails.managedByManagers}
                disabled={hasOwners}
                onChange={handleManagedBy}
              />
              <Form.Check
                id="managedBy_r_managers"
                type="radio"
                label="Managers"
                name="managedBy"
                value="Managers"
                defaultChecked={productDetails.managedByManagers && !productDetails.managedByMembers}
                disabled={hasOwners}
                onChange={handleManagedBy}
              />
            </>
          )}
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5} style={{ cursor: "pointer" }} onClick={() => setMembersCollapsed(!membersCollapsed)}>
          <i className={`fa fa-caret-${membersCollapsed ? "down" : "up"}`} />
          {!getManagedBy() ? "Members" : getManagedBy()}:
        </Form.Label>
        <Form.Label column sm={7}>
          {productDetails?.members?.length}
        </Form.Label>

        {!membersCollapsed && (
          <>
            <Col sm={12}>
              {productDetails.members.map((member, idx) => (
                <div
                  key={`${idx}-${hasOwners ? member.firstName : member.name}`}
                  className="d-flex mb-2 p-1"
                  style={{ border: "1px solid gray", backgroundColor: "#fafafa", borderRadius: "8px" }}
                >
                  <div style={{ flex: "1", color: "gray" }}>#{idx + 1}</div>
                  <div style={{ flex: "9" }}>
                    {hasOwners
                      ? `${member.firstName || ""} ${member.middleName || ""} ${member.lastName || ""}`
                      : member.name}
                  </div>
                  <div style={{ flex: "2" }}>
                    <Button
                      size="sm"
                      type="button"
                      variant="danger"
                      className="ml-2 py-0 pull-right"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMemberDelete(idx);
                      }}
                    >
                      <i className="fa fa-trash" />
                    </Button>
                  </div>
                </div>
              ))}
            </Col>
            <Form.Label column className="d-flex">
              {!hasOwners && (
                <Form.Control
                  size="sm"
                  value={newMember}
                  onChange={({ target: { value } }) => {
                    setNewMember(value);
                  }}
                  onKeyPress={handleNewMemberKeyPressed}
                />
              )}
              {hasOwners && (
                <Row>
                  <Col xs={4}>
                    <Form.Control
                      size="sm"
                      placeholder="First Name *"
                      value={newOwnerFirstName}
                      onChange={({ target: { value } }) => {
                        setNewOwnerFirstName(value);
                      }}
                      onKeyPress={handleNewMemberKeyPressed}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Control
                      size="sm"
                      placeholder="Middle Name"
                      value={newOwnerMiddleName}
                      onChange={({ target: { value } }) => {
                        setNewOwnerMiddleName(value);
                      }}
                      onKeyPress={handleNewMemberKeyPressed}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Control
                      size="sm"
                      placeholder="Last Name *"
                      value={newOwnerLastName}
                      onChange={({ target: { value } }) => {
                        setNewOwnerLastName(value);
                      }}
                      onKeyPress={handleNewMemberKeyPressed}
                    />
                  </Col>
                </Row>
              )}
              <Button
                size="sm"
                type="button"
                variant="success"
                className="ml-2"
                onClick={(e) => {
                  e.preventDefault();
                  if (newMember) {
                    handleMemberAdd({ name: newMember });
                    setNewMember("");
                  }
                  if (newOwnerFirstName && newOwnerLastName) {
                    handleMemberAdd({
                      firstName: newOwnerFirstName,
                      middleName: newOwnerMiddleName,
                      lastName: newOwnerLastName,
                    });
                    setNewOwnerFirstName("");
                    setNewOwnerMiddleName("");
                    setNewOwnerLastName("");
                  }
                }}
              >
                Add
              </Button>
            </Form.Label>
          </>
        )}
      </Form.Group>

      <hr />
      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Social Security Number:
        </Form.Label>
        <Col sm={7}>
          <InputMask
            className="form-control form-control-sm"
            mask="999-99-9999"
            maskChar=" "
            name="ssn"
            placeholder="999-99-9999"
            pattern="^\d{3}-\d{2}-\d{4}$"
            value={productDetails?.owner?.ssn || ""}
            onChange={handleProductChange}
            onInvalid={() => handleAddError("Social Security Number")}
          />
        </Col>
      </Form.Group>

      <hr />
      <h6 className="font-weight-bold">Activity</h6>
      <ActivityIncEdit productDetails={productDetails} onError={handleAddError} onProductChange={handleProductChange} />

      {productDetails?.organizedState === "WV" && (
        <>
          <hr />
          <h6 className="font-weight-bold">West Virginia Questions</h6>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              What is your estimate of the annual gross income from this business location?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="annualGrossIncome"
                value={productDetails?.wvFormationQuestions?.annualGrossIncome || ""}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="MV_0_20000">$0 - $20,000</option>
                <option value="MV_20000_MAX">Over $20,000</option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Counting all business income for the previous year at any location, has this business grossed over $4,000?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="grossOver4000"
                value={parseBooleanValue(productDetails?.wvFormationQuestions?.grossOver4000)}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Does this business have a location in WV from which you will make retail sales or do any service
              maintenance work?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="locationInWV"
                value={parseBooleanValue(productDetails?.wvFormationQuestions?.locationInWV)}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Will this business sell at either wholesale or retail, or consume for business operations any of the
              following products?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="retailProduct"
                value={productDetails?.wvFormationQuestions?.retailProduct || ""}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="ALCOHOL">Alcoholic beverages</option>
                <option value="TOBACCO">Tobacco products</option>
                <option value="MOTOR_FUEL">Motor fuel</option>
                <option value="SOFT_DRINKS">Soft drinks</option>
                <option value="NONE">None of the above</option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Do any of these categories relate to the activity of your business?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="businessActivity"
                value={productDetails?.wvFormationQuestions?.businessActivity || ""}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="HEALTH_CARE">Health care</option>
                <option value="NATURAL_GAS">Natural gas</option>
                <option value="NATURAL_RESOURCES">Natural resources</option>
                <option value="PUBLIC_UTILITIES">Public utilities</option>
                <option value="TELECOMMUNICATION">Telecommunications</option>
                <option value="TRANSPORTATION">Transportation</option>
                <option value="NONE">None of the above</option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              What is your taxable year-end for federal tax purposes?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="taxableEndYearMonth"
                value={productDetails?.wvFormationQuestions?.taxableEndYearMonth || ""}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                {getMonthOptions().map(({ label, value }) => (
                  <option key={`taxableEndYearMonth-${value}`} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Do you have employees?
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                as="select"
                name="haveEmployees"
                value={parseBooleanValue(productDetails?.wvFormationQuestions?.haveEmployees)}
                onChange={handleWVFormationQuestionsChange}
              >
                <option value="">Please select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Col>
          </Form.Group>

          {productDetails?.wvFormationQuestions?.haveEmployees && (
            <>
              <hr />
              <h6 className="font-weight-bold">Employee Questions</h6>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  How many employees do you have working in WV?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    type="number"
                    min="0"
                    name="countOfEmployees"
                    value={productDetails?.wvFormationQuestions?.countOfEmployees || ""}
                    onChange={handleWVFormationQuestionsChange}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Will you pay wages to employees performing services in WV?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="payWagesInWV"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.payWagesInWV)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Will you have only casual employees—meaning three or fewer workers working not more than 10 days in
                  any quarter?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="onlyCasualEmployees"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.onlyCasualEmployees)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  What date did the first employee start working in WV?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    type="date"
                    name="firstEmployeeStartWork"
                    value={productDetails?.wvFormationQuestions?.firstEmployeeStartWork || ""}
                    onChange={handleWVFormationQuestionsChange}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Enter the date that the first employee was or will be paid:
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    type="date"
                    name="firstEmployeeWasPaid"
                    value={productDetails?.wvFormationQuestions?.firstEmployeeWasPaid || ""}
                    onChange={handleWVFormationQuestionsChange}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  What is the estimated gross wages paid in WV in the first calendar quarter of operations?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    type="number"
                    min="0"
                    name="grossWagesInWVFirstQuarter"
                    value={productDetails?.wvFormationQuestions?.grossWagesInWVFirstQuarter || ""}
                    onChange={handleWVFormationQuestionsChange}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Do you have or expect to have soon a quarterly payroll of $1,500 in WV?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="willPayrollBe1500"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.willPayrollBe1500)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Will you employ at least one WV worker in 20 different calendar weeks during a calendar year?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="willEmployWVWorkersIn20Weeks"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.willEmployWVWorkersIn20Weeks)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Will you withhold more than $250 of WV income tax per month?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="willWithholdMoreThan250Tax"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.willWithholdMoreThan250Tax)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  In what earliest quarter and year will the quarterly payroll reach or exceed $1,500?
                </Form.Label>
                <Col sm={7} />
                <Form.Label column sm={5}>
                  Quarter:
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    className="mb-1"
                    as="select"
                    name="earliestQuarterPayroll1500"
                    value={productDetails?.wvFormationQuestions?.earliestQuarterPayroll1500 || ""}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value={1}>Q1: January – March</option>
                    <option value={2}>Q2: April – June </option>
                    <option value={3}>Q3: July – September</option>
                    <option value={4}>Q4: October – December</option>
                  </Form.Control>
                </Col>
                <Form.Label column sm={5}>
                  Year:
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="earliestYearPayroll1500"
                    value={productDetails?.wvFormationQuestions?.earliestYearPayroll1500 || ""}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    {getYearOptions().map(({ label, value }) => (
                      <option key={`earliestYearPayroll1500-${value}`} value={value}>
                        {label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  In what earliest quarter and year will at least one worker be employed in 20 different calendar weeks
                  during a calendar year?
                </Form.Label>
                <Col sm={7} />
                <Form.Label column sm={5}>
                  Quarter:
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    className="mb-1"
                    as="select"
                    name="earliestQuarterWorkerEmployedIn20Weeks"
                    value={productDetails?.wvFormationQuestions?.earliestQuarterWorkerEmployedIn20Weeks || ""}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value={1}>Q1: January – March</option>
                    <option value={2}>Q2: April – June </option>
                    <option value={3}>Q3: July – September</option>
                    <option value={4}>Q4: October – December</option>
                  </Form.Control>
                </Col>
                <Form.Label column sm={5}>
                  Year:
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="earliestYearWorkerEmployedIn20Weeks"
                    value={productDetails?.wvFormationQuestions?.earliestYearWorkerEmployedIn20Weeks || ""}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    {getYearOptions().map(({ label, value }) => (
                      <option key={`earliestYearPayroll1500-${value}`} value={value}>
                        {label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row}>
                <Form.Label column sm={5}>
                  Is this business subject to federal unemployment tax?
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    as="select"
                    name="businessSubjectToFederalTax"
                    value={parseBooleanValue(productDetails?.wvFormationQuestions?.businessSubjectToFederalTax)}
                    onChange={handleWVFormationQuestionsChange}
                  >
                    <option value="">Please select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Control>
                </Col>
              </Form.Group>
            </>
          )}
        </>
      )}

      <hr />
      <h6 className="font-weight-bold">General questions</h6>
      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Is this your first LLC?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="questionsFirstLLC_r_yes"
            className="mr-3"
            type="radio"
            label="Yes"
            name="questionsFirstLLC"
            value={true}
            defaultChecked={productDetails.questionsFirstLLC}
            onChange={handleProductChange}
          />
          <Form.Check
            id="questionsFirstLLC_r_no"
            type="radio"
            label="No"
            name="questionsFirstLLC"
            value={false}
            defaultChecked={!productDetails.questionsFirstLLC}
            onChange={handleProductChange}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Will you have employees?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="questionsHaveEmployee_r_yes"
            className="mr-3"
            type="radio"
            label="Yes"
            name="questionsHaveEmployee"
            value={true}
            defaultChecked={productDetails.questionsHaveEmployee}
            onChange={handleProductChange}
          />
          <Form.Check
            id="questionsHaveEmployee_r_no"
            type="radio"
            label="No"
            name="questionsHaveEmployee"
            value={false}
            defaultChecked={!productDetails.questionsHaveEmployee}
            onChange={handleProductChange}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Have you started doing business?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="questionsStartedDoingBusiness_r_yes"
            className="mr-3"
            type="radio"
            label="Yes"
            name="questionsStartedDoingBusiness"
            value={true}
            defaultChecked={productDetails.questionsStartedDoingBusiness}
            onChange={handleProductChange}
          />
          <Form.Check
            id="questionsStartedDoingBusiness_r_no"
            type="radio"
            label="No"
            name="questionsStartedDoingBusiness"
            value={false}
            defaultChecked={!productDetails.questionsStartedDoingBusiness}
            onChange={handleProductChange}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Will you accept Credit Card Payments?
        </Form.Label>
        <Col sm={7} className="d-flex align-items-center">
          <Form.Check
            id="acceptCreditCardPayments_r_yes"
            className="mr-3"
            type="radio"
            label="Yes"
            name="acceptCreditCardPayments"
            value={true}
            defaultChecked={productDetails.acceptCreditCardPayments}
            onChange={handleProductChange}
          />
          <Form.Check
            id="acceptCreditCardPayments_r_no"
            type="radio"
            label="No"
            name="acceptCreditCardPayments"
            value={false}
            defaultChecked={!productDetails.acceptCreditCardPayments}
            onChange={handleProductChange}
          />
        </Col>
      </Form.Group>

      <hr />
      <h6 className="font-weight-bold mt-4">Primary Officer</h6>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          First Name:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required
            size="sm"
            type="text"
            name="firstName"
            value={productDetails.principalOfficer?.firstName || ""}
            onChange={handlePrincipalOfficerChange}
            onInvalid={() => handleAddError("First Name")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Middle Name (optional):
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="middleName"
            value={productDetails.principalOfficer?.middleName || ""}
            onChange={handlePrincipalOfficerChange}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Last Name:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required
            size="sm"
            type="text"
            name="lastName"
            value={productDetails.principalOfficer?.lastName || ""}
            onChange={handlePrincipalOfficerChange}
            onInvalid={() => handleAddError("Last Name")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Suffix Name (optional):
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            as="select"
            name="suffixName"
            value={productDetails?.principalOfficer?.suffixName || ""}
            onChange={handlePrincipalOfficerChange}
          >
            <option value="">Please select</option>
            {suffixArray.map((prop, key) => (
              <option key={key} value={prop}>
                {prop}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Phone:
        </Form.Label>
        <Col sm={7}>
          <InputMask
            className={clsx("form-control", "form-control-sm", {
              "is-invalid":
                productDetails?.principalOfficer?.phone &&
                !phoneNumRegexp.test(productDetails?.principalOfficer?.phone),
            })}
            mask="(999) 999-9999"
            maskChar=" "
            name="phone"
            placeholder="(999) 999-9999"
            pattern="((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}"
            value={productDetails?.principalOfficer?.phone || ""}
            onChange={handlePrincipalOfficerChange}
            onInvalid={() => handleAddError("Phone")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          E-mail:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="email"
            value={productDetails.principalOfficer?.email || ""}
            onChange={handlePrincipalOfficerChange}
          />
        </Col>
      </Form.Group>

      <hr />
      <h6 className="font-weight-bold mt-4">
        Registered Agent
        {!productDetails?.registeredAgent && (
          <Button
            className="px-2 py-0 ml-2"
            size="sm"
            variant="success"
            onClick={() => {
              handleRegisteredAgentChange({ target: { name: "firstName", value: null } });
            }}
          >
            Add
          </Button>
        )}
        {productDetails?.registeredAgent && (
          <Button
            className="px-2 py-0 ml-2"
            size="sm"
            variant="danger"
            onClick={() => {
              handleRegisteredAgentRemove();
            }}
          >
            Remove
          </Button>
        )}
      </h6>

      {productDetails?.registeredAgent && (
        <>
          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              First Name:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                required
                size="sm"
                type="text"
                name="firstName"
                pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-&]+$"
                minLength={1}
                maxLength={100}
                value={productDetails.registeredAgent?.firstName || ""}
                onChange={handleRegisteredAgentChange}
                onInvalid={() => handleAddError("Registered Agent First Name")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Middle Name:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                size="sm"
                type="text"
                name="middleName"
                pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-&]+$"
                value={productDetails.registeredAgent?.middleName || ""}
                onChange={handleRegisteredAgentChange}
                onInvalid={() => handleAddError("Registered Agent Middle Name")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Last Name:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                // required
                size="sm"
                type="text"
                name="lastName"
                pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-&]+$"
                minLength={0}
                maxLength={100}
                value={productDetails.registeredAgent?.lastName || ""}
                onChange={handleRegisteredAgentChange}
                onInvalid={() => handleAddError("Registered Agent Last Name")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Suffix Name:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                size="sm"
                as="select"
                name="suffixName"
                value={productDetails?.registeredAgent?.suffixName || ""}
                onChange={handleRegisteredAgentChange}
              >
                <option value="">Please select</option>
                {suffixArray.map((prop, key) => (
                  <option key={key} value={prop}>
                    {prop}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Phone:
            </Form.Label>
            <Col sm={7}>
              <InputMask
                className={clsx("form-control", "form-control-sm", {
                  "is-invalid":
                    productDetails?.registeredAgent?.phone &&
                    !phoneNumRegexp.test(productDetails?.registeredAgent?.phone),
                })}
                mask="(999) 999-9999"
                maskChar=" "
                name="phone"
                placeholder="(999) 999-9999"
                pattern="((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}"
                value={productDetails?.registeredAgent?.phone || ""}
                onChange={handleRegisteredAgentChange}
                onInvalid={() => handleAddError("Registered Agent Phone")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              E-mail:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                size="sm"
                type="text"
                name="email"
                value={productDetails.registeredAgent?.email || ""}
                onChange={handleRegisteredAgentChange}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mt-4">
            <Form.Label column sm={5}>
              Address1:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                // required
                size="sm"
                type="text"
                name="address1"
                // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                // pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
                // maxLength={30}
                // isInvalid={
                //   productDetails?.registeredAgent?.address?.address1
                //     ? !/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(
                //         productDetails?.registeredAgent?.address?.address1?.trim() || ""
                //       )
                //     : false
                // }
                value={productDetails?.registeredAgent?.address?.address1 || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent Address")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Address2:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                size="sm"
                name="address2"
                type="text"
                // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                // pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
                // maxLength={30}
                // isInvalid={
                //   productDetails?.registeredAgent?.address?.address2
                //     ? !/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(
                //         productDetails?.registeredAgent?.address?.address2?.trim() || ""
                //       )
                //     : false
                // }
                value={productDetails?.registeredAgent?.address?.address2 || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent Address2")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              City:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                // required
                size="sm"
                type="text"
                name="city"
                // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                // pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
                // maxLength={30}
                // isInvalid={
                //   productDetails?.registeredAgent?.address?.city
                //     ? !/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails?.registeredAgent?.address?.city || "")
                //     : false
                // }
                value={productDetails?.registeredAgent?.address?.city || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent City")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              State:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                // required
                size="sm"
                as="select"
                name="state"
                // isInvalid={
                //   productDetails?.registeredAgent?.address?.state ? !productDetails?.registeredAgent?.address?.state : false
                // }
                value={productDetails?.registeredAgent?.address?.state || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent State")}
              >
                <option value="">Select a state</option>
                {Object.keys(state_array).map((key) => (
                  <option key={key} value={key}>
                    {state_array[key]}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              Zip Code:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                // required
                size="sm"
                type="text"
                name="zip"
                maxLength={5}
                pattern="^\d{5}$"
                isInvalid={
                  productDetails?.registeredAgent?.address?.zip
                    ? !/\d{5}$/i.test(productDetails?.registeredAgent?.address?.zip || "")
                    : false
                }
                value={productDetails?.registeredAgent?.address?.zip || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent Zip")}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label column sm={5}>
              County:
            </Form.Label>
            <Col sm={7}>
              <Form.Control
                size="sm"
                type="text"
                name="county"
                // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                // pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
                // maxLength={30}
                // isInvalid={
                //   productDetails?.registeredAgent?.address?.county
                //     ? !/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails?.registeredAgent?.address?.county || "")
                //     : false
                // }
                value={productDetails?.registeredAgent?.address?.county || ""}
                onChange={handleRegisteredAgentAddressChange}
                onInvalid={() => handleAddError("Registered Agent County")}
              />
            </Col>
          </Form.Group>
        </>
      )}

      <hr />
      <h6 className="font-weight-bold mt-4">Corporate Address</h6>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Address1:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required
            size="sm"
            type="text"
            name="address1"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
            maxLength={30}
            isInvalid={!/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(productDetails.corporateAddress?.address1?.trim() || "")}
            value={productDetails.corporateAddress?.address1 || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate Address")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Address2:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            name="address2"
            type="text"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.corporateAddress?.address2
                ? !/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(productDetails.corporateAddress?.address2?.trim() || "")
                : false
            }
            value={productDetails.corporateAddress?.address2 || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate Address2")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          City:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required
            size="sm"
            type="text"
            name="city"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
            maxLength={30}
            isInvalid={!/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails.corporateAddress?.city || "")}
            value={productDetails.corporateAddress?.city || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate City")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          State:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            required
            as="select"
            name="state"
            isInvalid={!productDetails.corporateAddress?.state}
            value={productDetails.corporateAddress?.state || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate State")}
          >
            <option value="">Select a state</option>
            {Object.keys(state_array).map((key) => (
              <option key={key} value={key}>
                {state_array[key]}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Zip Code:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required
            size="sm"
            type="text"
            name="zip"
            maxLength={5}
            pattern="^\d{5}$"
            isInvalid={!/\d{5}$/i.test(productDetails.corporateAddress?.zip || "")}
            value={productDetails.corporateAddress?.zip || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate Zip")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          County:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="county"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.corporateAddress?.county
                ? !/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails.corporateAddress?.county || "")
                : false
            }
            value={productDetails.corporateAddress?.county || ""}
            onChange={handleCorporateAddressChange}
            onInvalid={() => handleAddError("Corporate County")}
          />
        </Col>
      </Form.Group>

      <h6 className="font-weight-bold mt-4">Mailing Address</h6>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Address1:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required={
              productDetails.mailingAddress?.zip ||
              productDetails.mailingAddress?.state ||
              productDetails.mailingAddress?.city
            }
            size="sm"
            type="text"
            name="address1"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.mailingAddress?.address1
                ? !/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(productDetails.mailingAddress?.address1?.trim() || "")
                : productDetails.mailingAddress?.zip ||
                  productDetails.mailingAddress?.state ||
                  productDetails.mailingAddress?.city
                ? true
                : false
            }
            value={productDetails.mailingAddress?.address1 || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing Address")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Address2:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            name="address2"
            type="text"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z0-9\\\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.mailingAddress?.address2
                ? !/^(?!\s*$)[ a-zA-Z0-9\\/-]+$/i.test(productDetails.mailingAddress?.address2?.trim() || "")
                : false
            }
            value={productDetails.mailingAddress?.address2 || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing Address2")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          City:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required={
              productDetails.mailingAddress?.address1 ||
              productDetails.mailingAddress?.state ||
              productDetails.mailingAddress?.zip
            }
            size="sm"
            type="text"
            name="city"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.mailingAddress?.city
                ? !/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails.mailingAddress?.city || "")
                : productDetails.mailingAddress?.address1 ||
                  productDetails.mailingAddress?.state ||
                  productDetails.mailingAddress?.zip
                ? true
                : false
            }
            value={productDetails.mailingAddress?.city || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing City")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          State:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required={
              productDetails.mailingAddress?.address1 ||
              productDetails.mailingAddress?.zip ||
              productDetails.mailingAddress?.city
            }
            size="sm"
            as="select"
            name="state"
            isInvalid={
              (productDetails.mailingAddress?.address1 ||
                productDetails.mailingAddress?.zip ||
                productDetails.mailingAddress?.city) &&
              !productDetails.mailingAddress?.state
            }
            value={productDetails.mailingAddress?.state || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing State")}
          >
            <option value="">Select a state</option>
            {Object.keys(state_array).map((key) => (
              <option key={key} value={key}>
                {state_array[key]}
              </option>
            ))}
          </Form.Control>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          Zip Code:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            required={
              productDetails.mailingAddress?.address1 ||
              productDetails.mailingAddress?.state ||
              productDetails.mailingAddress?.city
            }
            size="sm"
            type="text"
            name="zip"
            maxLength={5}
            pattern="^\d{5}$"
            isInvalid={
              productDetails.mailingAddress?.zip
                ? !/\d{5}$/i.test(productDetails.mailingAddress?.zip || "")
                : productDetails.mailingAddress?.address1 ||
                  productDetails.mailingAddress?.state ||
                  productDetails.mailingAddress?.city
                ? true
                : false
            }
            value={productDetails.mailingAddress?.zip || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing Zip")}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label column sm={5}>
          County:
        </Form.Label>
        <Col sm={7}>
          <Form.Control
            size="sm"
            type="text"
            name="county"
            // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
            pattern="^(?!\s*$)[ a-zA-Z\/\-]+$"
            maxLength={30}
            isInvalid={
              productDetails.mailingAddress?.county
                ? !/^(?!\s*$)[ a-zA-Z/-]+$/i.test(productDetails.mailingAddress?.county || "")
                : false
            }
            value={productDetails.mailingAddress?.county || ""}
            onChange={handleMailingAddressChange}
            onInvalid={() => handleAddError("Mailing County")}
          />
        </Col>
      </Form.Group>
    </div>
  );
}

export default FormLLCInc;
